import React from "react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Bug,
  Image as ImageIcon,
  User as UserIcon,
  Clock,
  Sparkle,
  UserCircle,
  RocketLaunch,
} from "@phosphor-icons/react";
import { BugReport, ItemType } from "shared/types/bug-report";
import { useUsers } from "shared/contexts/users.context";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";
import { PriorityStars } from "./PriorityStars";

interface BugReportCardProps {
  bug: BugReport;
  onClick: () => void;
  isHighlighted?: boolean;
}

export const BugReportCard: React.FC<BugReportCardProps> = ({
  bug,
  onClick,
  isHighlighted = false,
}) => {
  const { users } = useUsers();

  const getUserName = (userId: string) => {
    const foundUser = users.find((user) => user.id === userId);
    return foundUser
      ? `${foundUser.first_name} ${foundUser.last_name}`
      : "Unknown";
  };

  const typeConfig = {
    [ItemType.BUG]: {
      icon: Bug,
      color: "text-brick-primary",
      bgColor: "bg-brick-primary/10",
      prefix: "BUG",
    },
    [ItemType.FEATURE]: {
      icon: Sparkle,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      prefix: "FEAT",
    },
  };

  // Be defensive: backend may not provide a type yet
  const safeType =
    bug.type === ItemType.BUG || bug.type === ItemType.FEATURE
      ? bug.type
      : ItemType.BUG;
  const config = typeConfig[safeType] || {
    icon: Bug,
    color: "text-brick-primary",
    bgColor: "bg-brick-primary/10",
    prefix: "BUG",
  };
  const TypeIcon = config.icon;
  const timeAgo = formatDistanceToNow(new Date(bug.createdAt), {
    addSuffix: true,
    locale: nl,
  });

  return (
    <Card
      className={`cursor-pointer transition-all duration-200 ease-out hover:-translate-y-1 hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] ${
        isHighlighted ? "ring-moss-primary shadow-lg ring-2" : ""
      }`}
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="group flex min-w-0 flex-1 items-start gap-2">
            <TypeIcon
              weight="duotone"
              size={18}
              className={`${config.color} mt-0.5 shrink-0 transition-transform group-hover:scale-110`}
            />
            <h3 className="group-hover:text-moss-primary line-clamp-2 flex-1 text-sm font-medium transition-colors">
              {bug.title}
            </h3>
          </div>
          <div className="shrink-0">
            <PriorityStars priority={bug.priority} />
          </div>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs">
          <Badge
            className={`${config.bgColor} ${config.color} border-0 px-2 py-0.5 text-xs transition-all hover:scale-105`}
          >
            {safeType}
          </Badge>
          <span className="hover:text-moss-primary font-mono text-neutral-400 transition-colors">
            {config.prefix}-{bug.id}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="line-clamp-3 text-xs text-neutral-600">
          {bug.description}
        </p>

        {bug.tags && bug.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {bug.tags.slice(0, 3).map((tag, idx) => (
              <Badge
                key={idx}
                variant="outline"
                className="hover:border-moss-primary hover:text-moss-primary px-2 py-0 text-xs transition-all hover:scale-105"
              >
                {tag}
              </Badge>
            ))}
            {bug.tags.length > 3 && (
              <Badge
                variant="outline"
                className="hover:border-moss-primary hover:text-moss-primary px-2 py-0 text-xs transition-all hover:scale-105"
              >
                +{bug.tags.length - 3}
              </Badge>
            )}
          </div>
        )}

        {bug.epic && (
          <div
            className="bg-moss-primary/10 border-moss-primary/30 hover:bg-moss-primary/20 flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 transition-all"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `/bug-reports/epics/${bug.epic!.id}`;
            }}
            title={`Ga naar epic: ${bug.epic.title}`}
          >
            <RocketLaunch
              size={12}
              className="text-moss-primary shrink-0"
              weight="fill"
            />
            <span className="text-moss-primary truncate text-xs font-medium">
              {bug.epic.title}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between border-t pt-2 text-xs text-neutral-500">
          <div className="flex items-center gap-2">
            <div
              className="hover:text-moss-primary flex items-center gap-1 transition-all"
              title="Gemeld door"
            >
              <UserIcon
                size={14}
                className="transition-transform hover:scale-110"
              />
              <span>{getUserName(bug.reportedBy)}</span>
            </div>
            {bug.assignedTo && (
              <>
                <span className="text-neutral-300">→</span>
                <div className="flex items-center gap-1" title="Toegewezen aan">
                  <UserCircle
                    size={14}
                    weight="fill"
                    className="text-moss-primary"
                  />
                  <span className="text-moss-primary font-medium">
                    {getUserName(bug.assignedTo)}
                  </span>
                </div>
              </>
            )}
          </div>
          {bug.sharePointFolderUrl && (
            <a
              href={bug.sharePointFolderUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-moss-primary flex items-center gap-1 transition-all hover:scale-110"
              onClick={(e) => e.stopPropagation()}
              title="Open SharePoint folder"
            >
              <ImageIcon
                size={14}
                className="transition-transform hover:rotate-12"
              />
              <span className="font-semibold">
                {bug.screenshots?.length || 0}
              </span>
            </a>
          )}
        </div>

        <div className="flex items-center gap-1 text-xs text-neutral-400 transition-all hover:text-neutral-600">
          <Clock size={12} className="transition-transform hover:scale-110" />
          <span>{timeAgo}</span>
        </div>
      </CardContent>
    </Card>
  );
};
